import { useTranslation } from 'react-i18next'
import { getIn, useFormik } from 'formik'
import { ChangeEvent, MutableRefObject, useEffect, useState } from 'react'
import { HexColorPicker } from 'react-colorful'
import { useDebounce } from 'react-use'
import { defaultValues, getSortedNameTranslations, getValidationSchema } from 'components/forms/LabelForm/utils'
import { LabelFormFields } from 'components/forms/LabelForm/types'
import { Translation } from 'types/Translation'

import { useHasRole } from 'hooks/useHasRoles'
import { ADMIN_ROLE } from 'constants/roleGroups'
import * as Styled from './LabelForm.styles'

interface LabelFormProps {
  isEdit?: boolean
  initialValues?: LabelFormFields
  onSave: (values: LabelFormFields) => void
  submitRef: MutableRefObject<() => void>
}

const LabelForm: React.FC<LabelFormProps> = ({
  isEdit,
  initialValues,
  onSave: handleSave,
  submitRef,
}) => {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [colorValue, setColorValue] = useState(initialValues?.color || defaultValues.color)
  const isAdmin = useHasRole(ADMIN_ROLE)

  const [translationLanguages, setTranslationLanguages] = useState<string[]>(
    getSortedNameTranslations(initialValues?.nameTranslations)?.map(
      (value) => value.languageCode
    ) || []
  )

  const submit = (values: LabelFormFields) => {
    handleSave(values)
  }

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    setFieldValue,
  } = useFormik<LabelFormFields>({
    initialValues: {
      ...defaultValues,
      ...initialValues,
      nameTranslations: getSortedNameTranslations(initialValues?.nameTranslations) || [],
    },
    validationSchema: getValidationSchema(!!isEdit),
    onSubmit: submit,
    enableReinitialize: true,
  })

  const [, cancel] = useDebounce(() => {
    setFieldValue('color', colorValue)
  }, 500, [colorValue])

  useEffect(() => {
    if (initialValues?.color) {
      setColorValue(initialValues?.color)
      cancel()
    }
  }, [cancel, initialValues])

  useEffect(() => {
    if (submitRef?.current) {
      submitRef.current = handleSubmit
    }
    return () => {
      if (submitRef?.current) {
        submitRef.current = () => {}
      }
    }
  }, [handleSubmit, submitRef])

  const getValueForLanguageCode = (translations: Translation[], languageCode: string) =>
    translations.find((translation) => translation.languageCode === languageCode)?.value

  const handleChangeTranslationLanguages = (event: ChangeEvent<{ value: unknown }>) => {
    const newTranslationLanguages = (event.target.value as string[]).sort()
    setTranslationLanguages(newTranslationLanguages)
    const currentNameTranslations = values.nameTranslations
    setFieldValue(
      'nameTranslations',
      newTranslationLanguages.map((lng) => ({
        languageCode: lng,
        value: getValueForLanguageCode(currentNameTranslations, lng) || '',
      }))
    )
  }

  return (
    <Styled.Form>
      <Styled.InfoSection
        label={t('common.type')}
        editValueComponent={
          <Styled.LabelTypeSelect
            id='type'
            name='type'
            value={values.type}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={touched.type}
            disabled={!isAdmin}
          />
            }
      />
      <Styled.InfoSection
        label={t('common.name')}
        editValueComponent={
          <Styled.TextField
            id='name'
            name='name'
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={touched.name}
            errorMessage={t(errors.name || '')}
          />
        }
      />
      <Styled.InfoSection
        label={t('labels.translations')}
        editValueComponent={
          <Styled.TranslationLanguagesSelect
            multiple
            value={translationLanguages}
            onChange={handleChangeTranslationLanguages}
          />
            }
      />
      {values.nameTranslations.map((value, index) => (
        <Styled.InfoSection
          key={value.languageCode}
          label={`${t('common.name')} - ${value.languageCode}`}
          editValueComponent={
            <Styled.TextField
              id={`nameTranslations[${index}].value`}
              name={`nameTranslations[${index}].value`}
              value={values.nameTranslations[index].value}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={getIn(touched, `nameTranslations[${index}].value`)}
              errorMessage={t(getIn(errors, `nameTranslations[${index}].value`) || '')}
            />
              }
        />
      ))}
      <Styled.InfoSection
        label={t('labels.color')}
        editValueComponent={
          <>
            <Styled.ColorTextField
              errorMessage={t(errors.color || '')}
              id='color'
              name='color'
              value={values.color}
              placeholder={t('labels.colorNotSet')}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.color}
              InputProps={{
                disableUnderline: true,
                endAdornment: (
                  <Styled.ColorPickerContainer>
                    <Styled.ColorCircle $backgroundColor={values.color || '#ffffff'} />
                    <Styled.ColorPickerButton
                      onClick={(event) => setAnchorEl(event.currentTarget)}
                    />
                  </Styled.ColorPickerContainer>
                )
              }}
            />
            <Styled.ColorPopover
              open={!!anchorEl}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{
                vertical: 'center',
                horizontal: 'center',
              }}
            >
              <HexColorPicker
                color={values.color || '#ffffff'}
                onChange={setColorValue}
              />
            </Styled.ColorPopover>
          </>
            }
      />
    </Styled.Form>
  )
}

export { LabelForm }
